import React, { useEffect, useState } from 'react'
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo'
import { fmtTime, toneOf } from './liveUi'

const COUNTDOWN_SECONDS = 5

// After-tap banner (bottom centre): "<label> logged hh:mm:ss", quick chips,
// Add details, Undo, and a Close button that counts down and dismisses
// itself. Picking a chip restarts the countdown.
const TapPrompt = ({ prompt, onClose, onAddDetails, onUndo, onChip }) => {
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS)
  const [picked, setPicked] = useState(null)
  const promptId = prompt?.event?.id

  useEffect(() => {
    setRemaining(COUNTDOWN_SECONDS)
    setPicked(null)
  }, [promptId])

  useEffect(() => {
    if (!promptId) return undefined
    if (remaining <= 0) {
      onClose()
      return undefined
    }
    const id = setTimeout(() => setRemaining((value) => value - 1), 1000)
    return () => clearTimeout(id)
  }, [promptId, remaining, onClose])

  if (!prompt) return null
  const { action, event } = prompt
  const tone = toneOf(action?.tone)
  const label = event?.detail
    ? `${event.label} · ${event.detail}`
    : event?.label

  return (
    <Box
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: { xs: 12, md: 24 },
        transform: 'translateX(-50%)',
        width: 'min(720px, calc(100vw - 24px))',
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 1.5,
          borderRadius: 3,
          borderLeft: 8,
          borderLeftColor: tone.main,
        }}
      >
        <Stack gap={1}>
          <Typography fontWeight={800} fontSize="1.05rem">
            {label} logged {fmtTime(event?.time)}
          </Typography>
          {action?.chips?.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {action.chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  color={picked === chip ? 'primary' : 'default'}
                  variant={picked === chip ? 'filled' : 'outlined'}
                  disabled={!!picked}
                  onClick={() => {
                    setPicked(chip)
                    setRemaining(COUNTDOWN_SECONDS)
                    onChip(chip)
                  }}
                  sx={{
                    height: 44,
                    minWidth: 56,
                    fontSize: '1rem',
                    fontWeight: 700,
                  }}
                />
              ))}
            </Stack>
          )}
          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {prompt.request && (
              <Button
                variant="contained"
                onClick={onAddDetails}
                sx={{ minHeight: 48, flex: { xs: 1, sm: 'none' } }}
              >
                Add details
              </Button>
            )}
            <Button
              color="error"
              variant="outlined"
              startIcon={<UndoIcon />}
              onClick={onUndo}
              sx={{ minHeight: 48 }}
            >
              Undo
            </Button>
            <Button onClick={onClose} sx={{ minHeight: 48 }}>
              Close ({remaining})
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}

export default TapPrompt
